import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MultiSelect } from "@/components/ui/multi-select";
import { TabsContent } from "@/components/ui/tabs";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import statisticsAPIService, { type ReceptionTimeline } from "@/lib/statistics-api.service";
import { ApproverHeatmaps } from "./approver-heatmaps";
import { ReceptionHeatmap } from "./reception-heatmap";
import { StatisticsFilterControls, type StatisticsFilters } from "./StatisticsControls";
import { downloadChartPng, toAPIFilter } from "./utils";
import { Toast } from "@/lib/utils";

interface PacingTabProps {
	filters?: StatisticsFilters;
	onFiltersChange?: (filters: StatisticsFilters) => void;
	/** Tăng lên mỗi lần trang yêu cầu tải lại; đổi giá trị là fetch lại. */
	refreshToken?: number;
}

/** Tab "Nhịp độ": nhiệt độ tiếp nhận theo ngày × ô 30 phút. */
export default function PacingTab({
	filters = {},
	onFiltersChange,
	refreshToken = 0,
}: PacingTabProps) {
	const pacingRef = useRef<HTMLDivElement>(null);
	// `undefined` = lần tải đầu chưa xong; refetch giữ dữ liệu cũ, không rơi về
	// skeleton mỗi lần đổi bộ lọc (CLAUDE.md §1).
	const [timeline, setTimeline] = useState<ReceptionTimeline | undefined>(undefined);
	const [error, setError] = useState<string | null>(null);
	// Lọc người tiếp nhận nằm HẲN trong tab này, không vào `filters` dùng chung:
	// các tab khác không có khái niệm "người tiếp nhận" trong dữ liệu của họ.
	// Rỗng = hiện tất cả, cùng quy ước với "Tất cả khoa"/"Tất cả lịch hẹn".
	const [selectedApprovers, setSelectedApprovers] = useState<string[]>([]);
	const [exporting, setExporting] = useState(false);
	const approverFilterId = `${useId()}-approvers`;

	// Tab này KHÔNG áp phạm vi "Ngày tiếp nhận" của trang: bộ lọc đó đã gỡ khỏi
	// giao diện ở đây, nên vẫn gửi lên server thì nó lọc trong im lặng — người
	// dùng thấy một biểu đồ hẹp hơn thực tế mà không có control nào giải thích.
	const apiFilter = useMemo(() => {
		const { receptionDateRange: _ignored, ...rest } = filters;
		return toAPIFilter(rest, "total");
	}, [filters]);

	useEffect(() => {
		let cancelled = false;
		statisticsAPIService
			.getReceptionTimeline(apiFilter)
			.then((data) => {
				if (cancelled) return;
				setTimeline(data);
				setError(null);
			})
			.catch((err) => {
				if (cancelled) return;
				console.error("Error fetching reception timeline:", err);
				setError(err instanceof Error ? err.message : "Không tải được nhịp độ tiếp nhận");
			});
		return () => {
			cancelled = true;
		};
		// `refreshToken`: nút Tải lại của trang phải làm mới cả tab này.
	}, [apiFilter, refreshToken]);

	// Trạng thái chạy nằm ở CHÍNH cái nút (spinner + "Đang tải...") chứ không
	// phải một toast loading: `Toast.promise` chỉ nhận chuỗi tĩnh cho nhánh lỗi,
	// mà 403 ("chưa được cấp quyền") cần nói khác lỗi mạng — người đọc phải biết
	// mình cần xin quyền hay chỉ cần thử lại.
	const handleExport = async () => {
		setExporting(true);
		try {
			await statisticsAPIService.exportReceptionTimelineAsXLSX(apiFilter);
			Toast.success("Đã tải file nhịp độ tiếp nhận");
		} catch (err) {
			console.error("Error exporting reception timeline:", err);
			Toast.error(
				err instanceof Error && err.message.includes("403")
					? "Tài khoản chưa được cấp quyền xem thống kê Nhịp độ."
					: "Không tải được file. Thử lại sau ít phút.",
			);
		} finally {
			setExporting(false);
		}
	};

	const approverOptions = useMemo(
		() =>
			(timeline?.approvers ?? []).map((approver) => ({
				value: approver.approverId,
				label: approver.approverName,
				hint: `${approver.totalReceived.toLocaleString("vi-VN")} hồ sơ`,
			})),
		[timeline],
	);

	return (
		<TabsContent value="pacing">
			<div ref={pacingRef} className="space-y-4 md:space-y-6">
				<div className="flex items-center justify-end gap-2">
					<Button
						onClick={handleExport}
						variant="outline"
						size="sm"
						// Khoá nút VÀ hiện spinner ngay khi bấm: file dựng trên server và có
						// thể mất vài giây, khoá không mà không báo thì người dùng tưởng
						// hỏng rồi bấm tiếp (CLAUDE.md §3).
						disabled={exporting}
						className="flex items-center gap-2 w-fit"
					>
						{exporting ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<FileSpreadsheet className="h-4 w-4" />
						)}
						<span className="hidden sm:inline">{exporting ? "Đang tải..." : "Tải Excel"}</span>
						<span className="sm:hidden">{exporting ? "..." : "Excel"}</span>
					</Button>
					<Button
						onClick={() => downloadChartPng(pacingRef, "thong-ke-nhip-do")}
						variant="outline"
						size="sm"
						className="flex items-center gap-2 w-fit"
					>
						<Download className="h-4 w-4" />
						<span className="hidden sm:inline">Tải PNG</span>
						<span className="sm:hidden">PNG</span>
					</Button>
				</div>

				{onFiltersChange && (
					<StatisticsFilterControls
						filterType="total"
						onFilterChange={() => {}}
						showChartLimit={false}
						showFilterType={false}
						filters={filters}
						onFiltersChange={onFiltersChange}
						showFilters
						showReceptionDateRange={false}
					/>
				)}

				{/* Bộ lọc riêng của tab: chỉ đổi những lưới nhỏ bên dưới, KHÔNG đổi lưới
				    tổng — lưới tổng là mốc so sánh chung nên phải đứng yên khi người
				    dùng khoanh vùng vài người. */}
				<Card>
					<CardContent className="p-3">
						<div className="flex flex-col sm:flex-row sm:items-center gap-2">
							<label
								htmlFor={approverFilterId}
								className="text-xs font-medium text-muted-foreground whitespace-nowrap"
							>
								Người tiếp nhận:
							</label>
							<div className="w-full sm:w-72">
								<MultiSelect
									id={approverFilterId}
									options={approverOptions}
									values={selectedApprovers}
									onChange={setSelectedApprovers}
									placeholder="Tất cả người tiếp nhận"
									searchPlaceholder="Tìm theo tên..."
									emptyText="Chưa có người tiếp nhận nào trong phạm vi lọc"
								/>
							</div>
						</div>
					</CardContent>
				</Card>

				{error && (
					<p className="text-sm text-destructive" role="alert">
						{error}
					</p>
				)}

				<ReceptionHeatmap
					buckets={timeline?.buckets}
					totalReceived={timeline?.totalReceived ?? 0}
					peakHour={timeline?.peakHour ?? null}
				/>

				<ApproverHeatmaps approvers={timeline?.approvers} selectedIds={selectedApprovers} />
			</div>
		</TabsContent>
	);
}
